import { useContext, useEffect, useMemo, useState } from "react";

import { AppContexts } from "../../../providers";
import GoogleAutoComplete from "../../common/GoogleAutoComplete";

const BuilderRecommendationStep = ({ rental, categoryType, label }) => {
  const utilContext = useContext(AppContexts.UtilContext);
  const destinationContext = useContext(AppContexts.DestinationContext);
  const [address, setAddress] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (rental?.destination_id) {
      destinationContext.getSelectedDestination(rental.destination_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rental?.destination_id]);

  const recommendations = useMemo(
    () =>
      (destinationContext.destination?.recommendations || []).filter(
        (recommendation) => recommendation.tab_id === categoryType,
      ),
    [categoryType, destinationContext.destination?.recommendations],
  );

  const searchLocation = useMemo(() => {
    const destination = destinationContext.destination;
    const isSelectedDestination =
      String(destination?.destination_id || "") ===
      String(rental?.destination_id || "");
    const lat = isSelectedDestination ? destination?.lat : rental?.lat;
    const lng = isSelectedDestination ? destination?.lng : rental?.lng;

    if (lat == null || lng == null || lat === "" || lng === "") return "";
    return `${lat},${lng}`;
  }, [
    destinationContext.destination,
    rental?.destination_id,
    rental?.lat,
    rental?.lng,
  ]);

  const addRecommendation = async () => {
    if (!placeId) {
      setError("Select a place from the suggestions.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const place = await utilContext.place(placeId);
      await destinationContext.createRecommendation({
        google_place_id: place.place_id,
        address: place.formatted_address,
        name: place.name,
        lat: place.geometry?.location?.lat,
        lng: place.geometry?.location?.lng,
        destination_id: rental.destination_id,
        tab_id: categoryType,
        display_as: "place",
        attachments: [],
      });
      await destinationContext.getSelectedDestination(rental.destination_id);
      setAddress("");
      setPlaceId("");
    } catch (saveError) {
      setError(
        saveError?.response?.data?.error ||
          "We couldn't add that place. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="guide-onboarding-simple-panel">
      <p className="guide-onboarding-intro">
        Search Google Places and add useful {label.toLowerCase()} near your
        property. You can skip this and return later.
      </p>
      <div className="guide-onboarding-place-search">
        <GoogleAutoComplete
          width="100%"
          placeholder={`Search for ${label.toLowerCase()}`}
          address={address}
          location={searchLocation}
          onChange={(value) => {
            setAddress(value || "");
            setPlaceId("");
          }}
          onSelect={(value, option) => {
            setAddress(value);
            setPlaceId(option.text);
            setError("");
          }}
        />
        <button
          type="button"
          className="uk-button hms-btn hms-red-btn"
          onClick={addRecommendation}
          disabled={saving}
        >
          {saving ? "Adding…" : "Add place"}
        </button>
      </div>
      {error && <p className="guide-onboarding-error">{error}</p>}

      <div className="guide-onboarding-added-list">
        <h3>Added to {label}</h3>
        {recommendations.length === 0 ? (
          <p className="secondary">No places added yet.</p>
        ) : (
          <ul className="uk-list uk-list-divider">
            {recommendations.map((recommendation) => (
              <li key={recommendation.recommendation_id}>
                <strong>{recommendation.name}</strong>
                {recommendation.address && (
                  <span>{recommendation.address}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BuilderRecommendationStep;
